import React, { useState, useCallback } from 'react';
import {
  Container,
  Card,
  Button,
  Row,
  Col,
  Spinner,
  Breadcrumb,
  ProgressBar,
  Form
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faDownload,
  faUserTie,
  faFileContract,
  faClipboardCheck,
  faHandshake,
  faCheck,
  faIndustry,
  faUsers,
  faFileAlt,
  faTasks,
  faChartBar,
  faListAlt
} from '@fortawesome/free-solid-svg-icons';
import '../common.css';
import EventProfile from './EventProfile';
import EventSchedule from './EventSchedule';
import Attendance from './Attendance';
import Budget from './Budget';
import VenueManagement from './VenueManagement';
import SupplyChain from './SupplyChain';
import StaffPlanning from './StaffPlanning';
import TechnicalPlanning from './TechnicalPlanning';
import Documentation from './Documentation';
import ExecutionStrategy from './ExecutionStrategy';
import PostEventEvaluation from './PostEventEvaluation';
import ReviewSummary from './ReviewSummary';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { generateEventPlan } from '../../../service/workflow-api';

const steps = ["EVENT_DETAILS", "VENUE_MANAGEMENT", "SUPPLY_CHAIN", "STAFF_PLANNING", "TECHNICAL_PLANNING", "DOCUMENTATION", "EXECUTION_STRATEGY", "POST_EVENT_EVALUATION", "REVIEW_SUMMARY"];
const wizardLabels = ["Event Details", "Venue", "Supply Chain", "Staff", "Technical", "Documentation", "Execution", "Evaluation", "Result"];

const EventsPlanner = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    eventName: '',
    eventType: '',
    eventDates: '',
    expectedAttendance: 0,
    budgetParameters: '',
    venueRequirements: '',
    layoutConfiguration: '',
    facilityOperationNeeds: '',
    cateringRequirements: '',
    furnitureEquipmentNeeds: '',
    inventoryTracking: '',
    deliveryScheduling: '',
    staffRoles: '',
    skillsRequirements: '',
    shiftPlanning: '',
    trainingNeeds: '',
    avEquipment: '',
    networkConnectivity: '',
    productionElements: '',
    technicalTestingPlans: '',
    requiredPermits: '',
    runSheets: '',
    vendorContracts: '',
    complianceDocuments: '',
    dayOfEventTimeline: '',
    taskAssignments: '',
    liveTrackingOptions: '',
    contingencyPlans: '',
    feedbackCollectionMethods: '',
    metricsForSuccess: '',
    reportRequirements: '',
  });
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [planId, setPlanId] = useState(null);
  const history = useHistory();

  const handleFormChange = (event) => {
    const { name, value, options } = event.target;
    const selectedValues = [];
    if (options) {
      for (let i = 0; i < options.length; i++) {
        if (options[i].selected) {
          selectedValues.push(options[i].value);
        }
      }
    }
    setFormData({ ...formData, [name]: options ? selectedValues : value });
  };

  const downloadPlan = async () => {
    setLoading(true);
    setSummaryLoading(true);
    try {
      // Construct summary
      const constructedSummary = Object.entries(formData)
        .filter(([key, value]) => value) // Filter out empty values
        .map(([key, value]) => ({
          key: key,
          value: value.toString()
        }));
      setSummary(constructedSummary);
    } finally {
      setLoading(false);
      setSummaryLoading(false);
    }
  };

  const nextStep = useCallback(() => {
    if (currentStep === steps.length - 2) {
      setLoading(true);
      setSummaryLoading(true);
      generateEventPlan(formData)
        .then(data => {
          // Construct summary
          const constructedSummary = Object.entries(formData)
            .filter(([key, value]) => value) // Filter out empty values
            .map(([key, value]) => ({
              key: key,
              value: value.toString()
            }));
          setSummary(constructedSummary);
          setPlanId(data.plan_id);
        })
        .finally(() => {
          setLoading(false);
          setSummaryLoading(false);
          setCurrentStep(prev => prev + 1);
        });
    } else {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, formData, generateEventPlan, setLoading, setSummaryLoading]);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => prev - 1);
  }, []);

  const getProgressPercentage = () => {
    return ((currentStep + 1) / steps.length) * 100;
  };

  const renderProgress = () => {
    const totalSteps = steps.length;
    const icons = [faUserTie, faFileContract, faClipboardCheck, faHandshake, faUsers, faIndustry, faFileAlt, faTasks, faChartBar, faListAlt];
    const currentProgress = loading ? currentStep + 0.5 : currentStep;
    
    return (
      <div className="mb-4">
        <Row className="text-center mb-2">
          {wizardLabels.map((label, index) => (
            <Col key={index}>
              <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${
                currentProgress > index ? 'bg-primary text-white' :
                currentProgress === index ? 'bg-dark-accent text-primary border border-primary' :
                'bg-dark-accent text-muted'
              }`} style={{width: '40px', height: '40px'}}>
                {currentProgress > index ?
                  <FontAwesomeIcon icon={faCheck} size="sm" /> :
                  loading && Math.floor(currentProgress) === index ?
                    <Spinner animation="border" size="sm" /> :
                    <FontAwesomeIcon icon={icons[index]} size="sm" />
                }
              </div>
              <div className="mt-1">
                <small className={currentProgress >= index ? 'text-primary font-weight-bold' : 'text-muted'}>
                  {label}
                </small>
              </div>
            </Col>
          ))}
        </Row>
        <ProgressBar now={getProgressPercentage()} variant="primary" />
      </div>
    );
  };

  
    const renderStep = () => {
      switch (steps[currentStep]) {
        case "EVENT_DETAILS":
          return (<>
                <EventProfile formData={formData} handleFormChange={handleFormChange} />
                <EventSchedule formData={formData} handleFormChange={handleFormChange} />
                <Attendance formData={formData} handleFormChange={handleFormChange} />
                <Budget formData={formData} handleFormChange={handleFormChange} />
                </>
          );
        case "VENUE_MANAGEMENT":
          return (
                <VenueManagement formData={formData} handleFormChange={handleFormChange} />
          );
        case "SUPPLY_CHAIN":
          return (
                <SupplyChain formData={formData} handleFormChange={handleFormChange} />
          );
        case "STAFF_PLANNING":
          return (
                <StaffPlanning formData={formData} handleFormChange={handleFormChange} />
          );
        case "TECHNICAL_PLANNING":
          return (
                <TechnicalPlanning formData={formData} handleFormChange={handleFormChange} />
          );
        case "DOCUMENTATION":
          return (
                <Documentation formData={formData} handleFormChange={handleFormChange} />
          );
        case "EXECUTION_STRATEGY":
          return (
                <ExecutionStrategy formData={formData} handleFormChange={handleFormChange} />
          );
        case "POST_EVENT_EVALUATION":
          return (
                <PostEventEvaluation formData={formData} handleFormChange={handleFormChange} />
          );
        case "REVIEW_SUMMARY":
          return (
            <ReviewSummary formData={formData} loading={summaryLoading} summary={summary} planId={planId} />
          );
        default:
          return null;
      }
    };
  return (
    <><Breadcrumb className="dark-breadcrumb mb-4">
    <Breadcrumb.Item href="/ai-workflows">AI Workflows</Breadcrumb.Item>
    <Breadcrumb.Item active>Events Logistics and Staff Planner</Breadcrumb.Item>
  </Breadcrumb>
    <Container fluid className="budget-planner-container py-4 min-vh-100">
      
              <div className="mb-5 text-center px-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <p 
                    className="display-5 mb-0 text-primary cursor-pointer" 
                    style={{ 
                      fontWeight: 200, 
                      textShadow: '0 0 10px rgba(79, 209, 197, 0.3)', 
                      fontSize: "1.8rem",
                      cursor: "pointer" 
                    }}
                    // onClick={navigateToHome}
                  >
                    AI Event Logistics & Staff Planner
                  </p>
                  <Button 
                    variant="outline-primary"
                    className="dark-outline-button"
                    onClick={() => history.push("/ai-workflows/ai-logistics-staff-planning/saved")}
                  >
                    History
                  </Button>
                </div>
              
              </div>


      <div className="mb-5 text-center">
        {renderProgress()}
      </div>

      <Card className="dark-main-card">
        <Card.Body className="p-4">
          {renderStep()}

          {loading && (
            <div className="text-center mt-4">
              <Spinner animation="border" variant="primary" />
            </div>
          )}

          <div className="d-flex justify-content-between mt-4">
            <Button
              variant="outline-secondary"
              onClick={prevStep}
              disabled={currentStep === 0 || loading}
              className="dark-outline-button"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
              Back
            </Button>
            {currentStep < steps.length - 1 ? (<Button
              variant="primary"
              onClick={() => {
                if (currentStep < steps.length - 1) {
                  nextStep();
                } else {
                  downloadPlan();
                }
              }}
              disabled={loading}
              className="dark-primary-button"
            >
              
                <>
                  Next
                  <FontAwesomeIcon icon={faChevronRight} className="ms-1" />
                </>
            </Button>) : null}
          </div>
        </Card.Body>
      </Card>
    </Container></>
  );
};

export default EventsPlanner;